<template>
  <div id="result" class="tags-container">
    <div
      v-for="(organizationMention, key) in mentions"
      :id="`result-${stringToClass(organizationMention.name)}`"
      :key="key"
      :class="{ active: stringToClass(organizationMention.name) === activeOrganization }"
      class="tag-wrapper input-ui click-outside-categories-ignore"
      @click="(e) => onOrganizationMentionsClick(e, organizationMention.name)"
    >
      <i
        v-if="!mentionsLoading"
        role="button"
        aria-pressed="false"
        class="icon-btn icon-delete fas fa-times"
        @click.prevent="deleteOrganizationMention(organizationMention)"
      ></i>
      <i 
        v-else
        class="icon-loading fa fa-spinner fa-spin"
      ></i>

      <app-mention-tag
        :parentContainerId="'result-'+stringToClass(organizationMention.name)"
        :tag="organizationMention"
        :categoryMentionKeys="categoryMentionKeys[stringToClass(organizationMention.name)]"
        :mentions="organizationMention.tags"
        :activeOrganization="activeOrganization"
        :keysPressed="keysPressed"
        :isActive="stringToClass(organizationMention.name) === activeOrganization"
        @updateMentionSentiment="$emit('updateMentionSentiment', $event)"
        @recordAction="$emit('recordAction', $event)"
        @showRelationTagMenu="$emit('showRelationTagMenu', $event)"
      />
      <span v-if="showScoringSystem" class="sentiment neutral">{{organizationMention.total_score || 0}}</span>

      <app-category-mention-container
          v-for="(category, key) in filteredCategoryMentionKeys(organizationMention)"
          :key="key"
          :organization="organizationMention"
          :categoryKey="category"
          :mentions="organizationMention.tags.filter(tag => (category === TAG_CATEGORY.SOURCES && tag.category === category) || (tag.parent === category) )"
          :parentContainerId="'result-'+stringToClass(organizationMention.name)"
          :activeOrganization="activeOrganization"
          :isActive="stringToClass(organizationMention.name) === activeOrganization"
          :keysPressed="keysPressed"
          @getTagScore="getTagScore"
          @deleteMentionTag="callDeleteMentionTag"
          @deleteRelationMentionTag="$emit('deleteRelationMentionTag', $event)"
          @updateMentionSentiment="$emit('updateMentionSentiment', $event)"
          @recordAction="$emit('recordAction', $event)"
          @showRelationTagMenu="$emit('showRelationTagMenu', $event)"
      />
      <div class="clone-mentions-btn far fa-clone"
           v-if="organizationMention.tags.length > 0"
           v-on:click="(e) => cloneMentions(e, organizationMention)"></div>
      <div class="clearfix" />
    </div>

    <app-form-modal
        v-if="isCloneMentionsModalOpen"
        title="clone_mentions"
        :customWrapperCss="'clone_mentions_modal'"
        :fields="cloneFields"
        :fieldsAdditions="addFieldsAdditions"
        :addOverflow="this.addFieldsAdditions.organization.options.length > 1"
        submitButtonLabel="form.button.clone"
        @onSave="cloneOrganizationMentions"
        @updateModel="handleMentionTypeChange"
        @closeModal="closeConeMentionsModal"
        @onSearchChangeInput="handleSearchChangeInput"
        :disableSubmit="isOrganizationsTagsLoading"
    />

  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import _ from 'lodash';

  import MentionTag from './mention_tag';
  import CategoryMentionContainer from './category_mention_container';
  import FormModal from '../form-modal';

  import editor from '../../helpers/editor';
  import { TAG_CATEGORY } from '../../config/const';

  export default {
    props: {
      mentions: {
        type: Array,
        required: true
      },
      activeOrganization: String,
      keysPressed: {
        type: Array,
        required: true
      }
    },
    components: {
      'app-category-mention-container': CategoryMentionContainer,
      'app-mention-tag': MentionTag,
      'app-form-modal': FormModal
    },
    watch: {
      isCloneMentionsModalOpen(newValue){
        if(newValue && this.isUseOrgWithCategory && !this.cloneFields?.includes('cat_id')) this.cloneFields.unshift('cat_id');
      },
      'tags.organizationData': {
        handler(tags){
          if(this.isUseOrgWithCategory) this.organizationOptionsData = this.organizationOptions;
        }
      }
    },
    computed: {
      ...mapState('tags', [
        'tags'
      ]),
      ...mapState('editorProjectSettings', [
        'editorProjectSettings'
      ]),
      ...mapState({
        projectSetupGeneral: state => state?.projectSetup?.projectSetup?.data?.general,
        projectSetupCategories: state => state?.projectSetup?.projectCategorySetup?.data,
        isOrganizationsTagsLoading: state => state?.tags?.tags?.organizationIsBusy,
        mentionsLoading: state => state.analysisArticle.mentions.isBusy
      }),
      organizationOptions() {
        return _.map(this.tags.organizationData.data, org => {
            return {
              label: org.name,
              value: editor.stringToClass(org.name),
              parent_tag_id: org?.parent_tag_id
          }
        })
      },
      mentionCategoryOptions() {
        return this.projectSetupCategories.reduce((acc, cate) => {
          if(cate?.id === this.organizationCategoryId && cate?.tags?.length){
            acc = [
              ...acc, 
              ...cate?.tags?.map(t => {
                return {
                  label: t?.name,
                  value: t?.id || editor.stringToClass(t?.name)
                }
              })
            ];
          }
          return acc;
        }, []);
      },
      showScoringSystem () {
        return this.editorProjectSettings.data && this.editorProjectSettings.data.scoring_system && this.editorProjectSettings.data.scoring_system.toLowerCase() !== "none";
      },
      categoryMentionKeys () {
        let categoryMentionKeys = {};
        this.mentions.map(organization => {
          const organizationName = this.stringToClass(organization.name);
          categoryMentionKeys[organizationName] = this.getContentCategoryKeyList(organizationName);
        });
        return categoryMentionKeys;
      },
      addFieldsAdditions () {
        let _fields = {
          organization: {
            style: 'flex-grow: 1;',
            options: this.isUseOrgWithCategory ? this.organizationOptionsData : this.organizationOptions?.filter(org => org.value !== this.cloneMention),
            loading: this.isOrganizationsTagsLoading,
            hideSelected: true,
            showNoResults: true,
            internalSearch: false,
            preselectFirst: false
          }
        };
        if(this.isUseOrgWithCategory) {
          _fields.cat_id = {
            style: 'flex-grow: 1;margin-right: 1rem;',
            options: this.mentionCategoryOptions || [],
            preselectFirst: true
          };
        }
        return _fields;
      },
      isUseOrgWithCategory() { return this.projectSetupGeneral?.organizations_with_category },
      organizationCategoryId() { return this.projectSetupGeneral?.organizations_category_id },
      projectId() { return this.$route.params.project_id; },
    },
    methods: {
      ...mapActions({
        'getOrganizationTags': 'tags/getOrganizationTags',
      }),
      callDeleteMentionTag({ tag, organization }) {
        this.$emit('deleteMentionTag', { tag, organization });
      },
      stringToClass (string) {
        return editor.stringToClass(string);
      },
      getTagScore (tag, positive) {
        return editor.getTagScore(tag, positive)
      },
      onOrganizationMentionsClick (e, organizationName) {
        if (this.activeOrganization !== this.stringToClass(organizationName)) {
          this.$emit('setActiveOrganization', this.stringToClass(organizationName));
        }
        else {
          const overflow = {
            enabled: true,
            xCoordinate: e.clientX,
            yCoordinate: e.clientY
          };

          //Do not trigger Categories Menu on clicking delete Org Mentions
          if (e.target.classList.contains('icon-loading') || e.target.classList.contains('icon-delete')) {
            return;
          }

          const categoryKey = e.target.closest('[category-key]') ? e.target.closest('[category-key]').getAttribute('category-key') : undefined;
          //Show overflowing menu
          this.$emit('toggleCategories', overflow, parseInt(categoryKey));
        }
      },
      //get a list of category keys, mentioned in the organization
      getContentCategoryKeyList (organization) {
        let categoryKeys = [];

        const categoryMentionKeys = _.find(this.mentions, mention => editor.stringToClass(mention.name) === organization);
        if(categoryMentionKeys && categoryMentionKeys.tags) {
          categoryMentionKeys.tags.map(mention => {
            let categoryKey = mention.parent;
            if(mention.category === TAG_CATEGORY.SOURCES) {
              categoryKey = TAG_CATEGORY.SOURCES;
            }

            if([TAG_CATEGORY.ARTICLE_LEVEL, TAG_CATEGORY.ARTICLELEVEL, TAG_CATEGORY.GROUPING, TAG_CATEGORY.ORGANIZATION].indexOf(mention.category) === -1 &&
              categoryKey && categoryKeys.indexOf(categoryKey) === -1) {
              categoryKeys.push(categoryKey);
            }
          });
        }
        return categoryKeys;
      },
      deleteOrganizationMention (orgMention) {
        //log mentions before removal of the whole organization mention
        this.$emit('recordAction', orgMention);
        this.$emit('deleteOrganizationMention', this.stringToClass(orgMention.name));
      },
      cloneMentions (e, organizationMention) {
        e.preventDefault();
        e.stopPropagation();

        this.cloneMention = this.stringToClass(organizationMention.name);
        this.isCloneMentionsModalOpen = true;
      },
      closeConeMentionsModal () {
        this.cloneMention = undefined;
        this.isCloneMentionsModalOpen = false;
        this.categoryId = null;
        this.organizationOptionsData = [];
      },
      cloneOrganizationMentions (cloneTo) {
        this.$emit('cloneOrganizationMentions', {cloneFrom: this.cloneMention, cloneTo: cloneTo.organization ? cloneTo.organization : cloneTo});
        this.closeConeMentionsModal();
      },
      filteredCategoryMentionKeys (organizationMention) {
        let _cmk = _.cloneDeep(this.categoryMentionKeys[this.stringToClass(organizationMention.name)]);
        if(this.isUseOrgWithCategory && organizationMention?.tags) {
          const mentionType = organizationMention.tags.find(ele=> ele.cat_id === this.organizationCategoryId);
          return _cmk.filter(ele => ele != mentionType.parent)
        }
        return _cmk;
      },
      handleMentionTypeChange(model, field){
        if(this.isUseOrgWithCategory && field?.name === 'cat_id' && model?.cat_id !== this.categoryId) {
          this.categoryId = model?.cat_id;
          // TODO: we may need to pass search in the future
          this.getOrganizationTags({
            projectId: this.projectId,
            // searchQuery: searchQuery,
            parentTagId: model?.cat_id
          });
        }
      },
      handleSearchChangeInput(value = '', field){
        if(field?.name === 'organization'){
          this.getOrganizationTags({
            projectId: this.projectId,
            searchQuery: (''+value)?.trim(),
            parentTagId: this.categoryId
          });
        }
      }
    },
    data () {
      return {
        TAG_CATEGORY: TAG_CATEGORY,
        isCloneMentionsModalOpen: false,
        cloneMention: undefined,
        organizationOptionsData: [],
        cloneFields: ['organization'],
        categoryId: null
      }
    }
  }
</script>

<style scoped>
  .icon-loading {
    float: right;
    margin-top: -5px;
    font-size: 15px;
  }
</style>